export type Environment = "SANDBOX" | "PRODUCTION";

export const ENV_STORAGE_KEY = "emicash_environment";

// ─── Environment ─────────────────────────────────────────────────────────────

export function getActiveEnvironment(): Environment {
  if (typeof window === "undefined") return "PRODUCTION";
  const stored = localStorage.getItem(ENV_STORAGE_KEY);
  return stored === "SANDBOX" ? "SANDBOX" : "PRODUCTION";
}

// ─── In-memory access token store ────────────────────────────────────────────
// Access tokens are kept in memory only — never written to document.cookie.
// Refresh tokens live in HttpOnly cookies managed by /api/auth/* route handlers.

const memoryTokens = new Map<Environment, string>();

export function getTokenForEnv(env: Environment): string | null {
  return memoryTokens.get(env) ?? null;
}

export function setTokenForEnv(env: Environment, token: string): void {
  memoryTokens.set(env, token);
}

export function clearTokenForEnv(env: Environment): void {
  memoryTokens.delete(env);
}

export function hasTokenForEnv(env: Environment): boolean {
  return memoryTokens.has(env);
}

// Used by the Axios interceptor (no React context available there).
export function getActiveToken(): string | null {
  return getTokenForEnv(getActiveEnvironment());
}

// ─── Session readiness gate ───────────────────────────────────────────────────
// Resolved once AuthContext has finished the async session-restore attempt.
// The Axios request interceptor awaits this before attaching the Authorization
// header, so no API call fires with an empty token while restore is in flight.

let _markSessionReady!: () => void;
export const sessionReady: Promise<void> = new Promise<void>((resolve) => {
  _markSessionReady = resolve;
});
export function markSessionReady(): void {
  _markSessionReady();
}

// ─── Migration from old single-cookie scheme ──────────────────────────────────
// Clear legacy client-side token cookies. Users will be prompted to re-login once.

export function migrateOldTokens(): void {
  if (typeof document === "undefined") return;

  const legacyCookies = [
    "auth_token",
    "refresh_token",
    "sandbox_auth_token",
    "production_auth_token",
  ];

  for (const name of legacyCookies) {
    const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
    if (match) {
      document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax`;
    }
  }

  const oldUser = localStorage.getItem("auth_user");
  if (oldUser && !localStorage.getItem("auth_user_production")) {
    localStorage.setItem("auth_user_production", oldUser);
    localStorage.removeItem("auth_user");
  }
}
